import { ExtendedPlatformContext } from '../../../../types/platformApi';
import { AppInstallOption } from '@wix/editor-platform-host-integration-apis';
import { BIReporter } from './bi';
import { installUnifiedComponents } from './installComponents';
import {
  createUnifiedComponentsInstallationError,
  UnifiedComponentsInstallationErrorCode as ErrorType,
} from './errors';
import {
  ComponentType,
  EditorReactComponent,
} from '@wix/ambassador-devcenter-components-v1-component/types';
import {
  AddAppData,
  AppData,
  UnifiedComponentsCollection,
  UnifiedPage,
  UnifiedLightbox,
  UnifiedWidget,
  UnifiedComponentAddedToTheStageData,
} from '../../../../types/unifiedComponents';
import { CodePackageComponentData } from '@wix/ambassador-app-service-webapp/types';
import { autoPopulatePlugins } from './pluginsAutoPopulate';
import { TOKEN } from './addComponents/addUnifiedComponents';

function getUnifiedComponentsCollection(
  appData: AppData,
): Promise<UnifiedComponentsCollection> {
  const unifiedComponents: UnifiedComponentsCollection = {
    widgets: {},
    builderComponents: {},
    pages: {},
    codePackages: [],
  };

  return new Promise((resolve) => {
    appData.components?.forEach(({ data, type, componentId, name }) => {
      if (!data) {
        return;
      }
      switch (type) {
        case ComponentType.STUDIO_WIDGET:
        case ComponentType.CUSTOM_ELEMENT_WIDGET:
          if (componentId) {
            unifiedComponents.widgets[componentId] = {
              ...data,
              type,
              widgetId: componentId,
            } as unknown as UnifiedWidget;
          }
          break;
        case ComponentType.EDITOR_REACT_COMPONENT:
          if (componentId) {
            unifiedComponents.builderComponents[componentId] = {
              data: data as EditorReactComponent,
              type,
              componentId,
              name: name ?? '',
            };
          }
          break;
        case ComponentType.UNIFIED_LIGHTBOX:
          if (componentId) {
            unifiedComponents.pages[componentId] = {
              data: data as UnifiedLightbox,
              type: ComponentType.UNIFIED_LIGHTBOX,
            };
          }
          break;
        case ComponentType.UNIFIED_PAGE:
          if (componentId) {
            unifiedComponents.pages[componentId] = {
              data: data as UnifiedPage,
              type: ComponentType.UNIFIED_PAGE,
            };
          }
          break;
        case ComponentType.CODE_PACKAGE:
          unifiedComponents.codePackages.push(data as CodePackageComponentData);
          break;
        default:
          break;
      }
    });
    resolve(unifiedComponents);
  });
}

export function installSingleUnifiedComponentsApp(
  context: ExtendedPlatformContext,
  appData: AppData,
  biReporter: BIReporter,
  appsOptions?: AppInstallOption,
  finishInstallationCallback?: (appDefId: string, data: AddAppData) => void,
) {
  let collection: UnifiedComponentsCollection;
  let componentsAddedToTheStage: UnifiedComponentAddedToTheStageData = {
    pages: {},
    widgets: {},
  };

  const installAppUnifiedComponents = () =>
    installUnifiedComponents(context, biReporter, appData, collection);

  const setupIntentInstallConfig = async () => {
    await context.intentInstallServices.setupIntentInstallWidgetsConfigFromUnifiedCollection(
      collection,
      appData.appDefinitionName,
    );
  };

  function installCodePackages(): Promise<void> {
    return biReporter.withBI(
      'INSTALL_CODE_PACKAGES',
      { appDefinitionId: appData.appDefinitionId },
      function installCodePackagesIfNeeded() {
        return new Promise<void>((resolve, reject) => {
          if (collection.codePackages.length > 0) {
            context.documentServices.wixCode.codePackages
              .installCodeReusePkg(
                appData.appDefinitionId,
                appData?.appFields?.installedVersion as string,
              )
              .then(resolve)
              .catch((error) =>
                reject(
                  createUnifiedComponentsInstallationError(
                    ErrorType.addingCodePackagesFailed,
                    'Could not install code packages',
                  )
                    .withAppDefIds(appData.appDefinitionId)
                    .withParentError(error),
                ),
              );
          } else {
            resolve();
          }
        });
      },
    );
  }

  function runEditorScript(): Promise<void> {
    return biReporter.withBI(
      'PLATFORM_RUN_SCRIPTS',
      { appDefinitionId: appData.appDefinitionId },
      function runEditorScriptFromDM() {
        return new Promise<void>((resolve, reject) =>
          context.documentServices.platform.installationFlow.addToDocumentWithEditorScript(
            appData.appDefinitionId,
            {
              [appData.appDefinitionId]: appsOptions,
              singleAppCallback: () => resolve(),
              onError: (error: Error) =>
                reject(
                  createUnifiedComponentsInstallationError(
                    ErrorType.runningEditorScriptFailed,
                    'Could not run editor script',
                  )
                    .withAppDefIds(appData.appDefinitionId)
                    .withParentError(error),
                ),
            },
          ),
        );
      },
    );
  }

  function navigateToAppEndPage() {
    return new Promise<void>((resolve, reject) => {
      const resolveWhenNavigationDone = () => {
        resolve();
        biReporter.reportBI('NAVIGATE_TO_DEV_CENTER', {
          appDefinitionId: appData.appDefinitionId,
        });
      };

      const rejectWhenNavigationFailed = () =>
        reject(
          createUnifiedComponentsInstallationError(
            ErrorType.navigateToAppEndPageFailed,
            'Could not locate the component or navigate to the page after installation',
          ).withAppDefIds(appData.appDefinitionId),
        );

      const getWidgetToNavigate = () => {
        return Object.values(componentsAddedToTheStage.widgets)[0];
      };

      const getPageIdToNavigate = () => {
        return Object.values(componentsAddedToTheStage.pages)[0].id;
      };

      if (appsOptions?.isSilent) {
        resolve();
        return;
      }

      if (Object.values(componentsAddedToTheStage.widgets).length > 0) {
        context.platformApiMethods.editor.selection
          .locateAndHighlightComponentByCompRef(appData, TOKEN, {
            compRef: getWidgetToNavigate(),
          })
          .then(resolveWhenNavigationDone)
          .catch(rejectWhenNavigationFailed);
        return;
      } else if (Object.values(componentsAddedToTheStage.pages).length > 0) {
        context.documentServices.pages.navigateTo(
          getPageIdToNavigate(),
          resolveWhenNavigationDone,
          rejectWhenNavigationFailed,
        );
        return;
      } else {
        resolve();
      }
    });
  }

  function handlePlugins(): Promise<void> {
    return autoPopulatePlugins(
      context,
      appsOptions!,
      appData.appDefinitionId,
      appData.components,
    );
  }

  // TODO: AddAppData - callback with relevant data (success \ type)
  function triggerAppInstalledCallback() {
    const addAppData = {
      ...appData,
      success: true,
      type: 'unified-app',
    };
    return new Promise<AddAppData>((resolve) => {
      finishInstallationCallback?.(appData.appDefinitionId, addAppData);
      resolve(addAppData);
    });
  }

  return new Promise<AddAppData>((resolve, reject) =>
    getUnifiedComponentsCollection(appData)
      .then((componentsCollection) => {
        collection = componentsCollection;
      })
      .then(setupIntentInstallConfig)
      .then(installAppUnifiedComponents)
      .then((addToStageValues) => {
        componentsAddedToTheStage = addToStageValues;
      })
      .then(installCodePackages)
      .then(runEditorScript)
      .then(navigateToAppEndPage)
      .then(handlePlugins)
      .then(triggerAppInstalledCallback)
      .then(resolve)
      .catch(reject),
  );
}
